import { useMemo, useState } from "react";
import { Clock3 } from "lucide-react";
import {
  DirectorySearch,
  EmptyArticlesState,
  ReadArticleLink,
  SiteArticleFooter,
  SiteArticleHeader,
  StickyWhatsAppCTA,
} from "./components";
import ArticleSeo from "./ArticleSeo";
import {
  articleCategories,
  articleDirectory,
  defaultArticleWhatsAppUrl,
  SITE_URL,
} from "./articleData";

const directoryStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Zaviramed",
      url: SITE_URL,
      logo: `${SITE_URL}/logo_new_nobg.png`,
    },
    {
      "@type": "CollectionPage",
      "@id": `${SITE_URL}/articles#webpage`,
      name: "Health Articles",
      description:
        "Plain-language health articles from Zaviramed for Nigerian readers.",
      url: `${SITE_URL}/articles`,
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: SITE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Articles",
          item: `${SITE_URL}/articles`,
        },
      ],
    },
    {
      "@type": "ItemList",
      itemListElement: articleDirectory.map((article, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: article.url,
        name: article.title,
      })),
    },
  ],
};

export default function ArticlesIndex() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const filteredArticles = useMemo(() => {
    const cleanQuery = query.trim().toLowerCase();

    return articleDirectory.filter((article) => {
      const matchesCategory =
        category === "All" || article.category === category;
      const matchesQuery =
        !cleanQuery ||
        [
          article.title,
          article.excerpt,
          article.category,
          (article.keywords || []).join(" "),
        ]
          .join(" ")
          .toLowerCase()
          .includes(cleanQuery);

      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  return (
    <div className="articles-surface">
      <ArticleSeo
        title="Health Articles | Zaviramed"
        description="Read calm, practical health articles from Zaviramed, written for everyday Nigerian patients and designed to help you know when to speak with a doctor."
        canonical={`${SITE_URL}/articles`}
        image={`${SITE_URL}/doctor-patient.png`}
        type="website"
        structuredData={[directoryStructuredData]}
      />
      <SiteArticleHeader />

      <main
        id="main-content"
        className="zv-articles-directory mx-auto max-w-7xl px-5 pb-20 pt-12 lg:px-8"
      >
        <section className="zv-articles-hero mx-auto max-w-3xl text-center">
          <h1 className="mt-4 text-4xl font-semibold leading-tight text-slate-950 sm:text-5xl">
            Health Articles
          </h1>
          <p>
            Clear, practical health information. Understand your symptoms and
            know when to speak with a doctor.
          </p>
        </section>

        <section className="mx-auto mt-10 max-w-4xl">
          <DirectorySearch value={query} onChange={setQuery} />

          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {["All", ...articleCategories].map((item) => (
              <button
                key={item}
                type="button"
                aria-pressed={category === item}
                onClick={() => setCategory(item)}
                className={`article-focus rounded-full border px-4 py-2 text-sm font-black transition active:scale-[0.98] ${
                  category === item
                    ? "border-[#25D366] bg-[#25D366] text-[#05352e]"
                    : "border-white/60 bg-white/60 text-slate-600 hover:border-emerald-200 hover:text-[#075E54]"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-12 max-w-5xl">
          {filteredArticles.length === 0 ? (
            <EmptyArticlesState />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 lg:items-stretch">
              {filteredArticles.map((article) => (
                <article
                  key={article.slug}
                  className="border-[1px] border-gray-300 flex flex-col justify-between rounded-[28px] p-6 sm:p-8"
                >
                  <div>
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#075E54]">
                        {article.category}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500">
                        <Clock3 size={15} className="text-[#075E54]" />
                        {article.readingTime}
                      </span>
                    </div>
                    <h2 className="max-w-2xl text-2xl font-black leading-tight text-slate-950 sm:text-3xl">
                      {article.title}
                    </h2>
                    <p className="mt-4 max-w-2xl text-base  leading-8 text-slate-600">
                      {article.excerpt}
                    </p>
                  </div>
                  <div className="mt-7">
                    <ReadArticleLink to={`/articles/${article.slug}`} />
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      <SiteArticleFooter />
      <StickyWhatsAppCTA href={defaultArticleWhatsAppUrl} />
    </div>
  );
}
